import { Injectable } from '@angular/core';
import { Action, select, State, StateContext } from '@ngxs/store';
import { UserProfileState } from '@state/user-profile';
import {
	CreateBusinessEmailTemplate,
	DeleteBusinessEmailTemplate,
	LoadBusinessEmailTemplateVersions,
	UpdateBusinessEmailTemplate,
} from './business-email-templates/business-email-templates.actions';
import { BusinessEmailTemplatesSelectors } from './business-email-templates/business-email-templates.selectors';
import {
	CreateEmailTemplate,
	DeleteEmailTemplate,
	SelectEmailTemplate,
	UpdateEmailTemplate,
} from './email-templates.actions';
import { EmailTemplateState } from './email-templates.models';
import {
	CreateGlobalEmailTemplate,
	DeleteGlobalEmailTemplate,
	LoadGlobalEmailTemplateVersions,
	UpdateGlobalEmailTemplate,
} from './global-email-templates/global-email-templates.actions';
import { GlobalEmailTemplatesSelectors } from './global-email-templates/global-email-templates.selectors';

@State<EmailTemplateState>({
	name: 'emailTemplates',
	defaults: {
		selectedTemplateId: null,
		selectedTemplateType: null,
	},
})
@Injectable()
export class EmailTemplatesState {
	getBusinessEmailTemplateById = select(
		BusinessEmailTemplatesSelectors.$getTemplateByIdWithVersion,
	);
	getGlobalEmailTemplateById = select(
		GlobalEmailTemplatesSelectors.$getTemplateByIdWithVersion,
	);
	canEditEmailTemplate = select(UserProfileState.$canEditCloserEmailTemplates);
	profileId = select(UserProfileState.getProfileId);

	@Action(SelectEmailTemplate)
	selectTemplate(
		ctx: StateContext<EmailTemplateState>,
		action: SelectEmailTemplate,
	) {
		const businessTemplate = this.getBusinessEmailTemplateById()(
			action.templateId,
		);
		const templateType =
			businessTemplate.metadata !== null ? 'business' : 'global';

		ctx.patchState({
			selectedTemplateId: action.templateId,
			selectedTemplateType: templateType,
		});

		const template =
			templateType === 'business'
				? businessTemplate
				: this.getGlobalEmailTemplateById()(action.templateId);

		if (template?.metadata?.versionId && !template?.version) {
			if (templateType === 'business') {
				ctx.dispatch(
					new LoadBusinessEmailTemplateVersions(template.metadata.id),
				);
			} else {
				ctx.dispatch(new LoadGlobalEmailTemplateVersions(template.metadata.id));
			}
		}
	}

	@Action(CreateEmailTemplate)
	createTemplate(
		ctx: StateContext<EmailTemplateState>,
		action: CreateEmailTemplate,
	) {
		if (action.templateType === 'business') {
			ctx.dispatch(
				new CreateBusinessEmailTemplate(
					action.template,
					action.afterCreateAction,
				),
			);
		} else {
			ctx.dispatch(
				new CreateGlobalEmailTemplate(
					action.template,
					action.afterCreateAction,
				),
			);
		}
	}

	@Action(UpdateEmailTemplate)
	updateTemplate(
		ctx: StateContext<EmailTemplateState>,
		action: UpdateEmailTemplate,
	) {
		const state = ctx.getState();
		let metadata = action.templateMetadata;
		if (
			state.selectedTemplateType === 'global' &&
			!this.canEditEmailTemplate()
		) {
			metadata = {
				...metadata,
				source: 'global',
				createdBy: this.profileId(),
				createdAt: Date.now(),
			};
		}
		if (
			state.selectedTemplateType === 'business' ||
			!this.canEditEmailTemplate()
		) {
			ctx.dispatch(
				new UpdateBusinessEmailTemplate(
					action.templateId,
					metadata,
					action.templateVersion || {},
				),
			);
		} else {
			ctx.dispatch(
				new UpdateGlobalEmailTemplate(
					action.templateId,
					action.templateMetadata,
					action.templateVersion || {},
				),
			);
		}
	}

	@Action(DeleteEmailTemplate)
	deleteTemplate(
		ctx: StateContext<EmailTemplateState>,
		action: DeleteEmailTemplate,
	) {
		const state = ctx.getState();
		if (state.selectedTemplateType === 'business') {
			ctx.dispatch(new DeleteBusinessEmailTemplate(action.templateId));
		} else {
			ctx.dispatch(new DeleteGlobalEmailTemplate(action.templateId));
		}
	}
}
